// @flow
import React from 'react'
import { connect } from 'react-redux'
import { Redirect, Route, Switch } from 'react-router-dom'
import I18n from 'i18n-react'
import Alert from 'reactstrap/lib/Alert'
import Button from 'reactstrap/lib/Button'
import Modal from 'reactstrap/lib/Modal'
import ModalBody from 'reactstrap/lib/ModalBody'
import classNames from 'classnames'
import Loadable from 'react-loadable'
import GuardXLogger from 'guard-x-logger'

import { getProductState } from '../store/reducers.js'
// config
import { config } from '../Config/ConfigLoader'

import packageJson from '../../package.json'
// submodules
import { cookie } from 'devx-js-utilities'

import Api, { serverUrl } from '../Services/SuriApi'
import { getValueForPath } from '../Core/FilterFunctions'
import { getStepUrl } from '../Core/ContainerFunctions'

import passwords from '../Core/passwords.json'
// containers
import ProductDetailContainer from '../Containers/ProductDetailContainer'
// redux
import NavigationActions from '../Core/Redux/NavigationRedux'
import GeneralActions, { ERROR_TYPES, NOTIFICATION_TYPES } from '../Core/Redux/GeneralRedux'
import DebugActions from '../Core/Redux/DebugRedux'
import ProductActions from '../Core/Redux/ProductRedux'
import UserDataActions from '../Core/Redux/UserDataRedux'
import SettingsActions, { WEB_TYPES, WEB_TYPES_KEYS } from '../Core/Redux/SettingsRedux'
import DiscountsActions from '../Core/Redux/DiscountsRedux'
import QuoteActions from '../Core/Redux/QuoteRedux'
import ValidationActions from '../Core/Redux/ValidationRedux'
import LinkBuilderActions from '../Core/Redux/LinkBuilderRedux'
// components
import { GoogleTagManager, Loader, NavigationPanel, ValidatorError, ValidatorScript } from '../Components'
import { ShowDebugError, VariablesDebugBlock } from '../Components/_debug'

const QuoteListContainer = Loadable({
  loader: () => import('../Containers/QuoteListContainer'),
  loading() {
    return <span/>
  },
})

const CalculatorDetailContainer = Loadable({
  loader: () => import('../Containers/CalculatorDetailContainer'),
  loading() {
    return <span/>
  },
})

const ContractDraftContainer = Loadable({
  loader: () => import('../Containers/ContractDraftContainer'),
  loading() {
    return <span/>
  },
})

const AcceptDraftContainer = Loadable({
  loader: () => import('../Containers/AcceptDraftContainer'),
  loading() {
    return <span/>
  },
})

const ErrorContainer = Loadable({
  loader: () => import('../Containers/ErrorContainer'),
  loading() {
    return <span/>
  },
})

// types
declare var API_LINK: string
declare var DEV: boolean
import type { Product } from '../Core/Types/Product'
import type { ActiveSettingsType } from '../Core/Types/SettingsTypes'
import type { LocationType } from '../Core/Types/LocationType'
import type { AllErrorType } from '../Core/Types/ErrorTypes'
import type { StepsType } from '../Core/Types/Navigation'
import type { ValidationErrorType } from '../Core/Types/ValidationError'
import type { ResourcesType, ResourceType } from '../Core/Types/Resources'
import type { NotificationType } from '../Core/Redux/GeneralRedux'

type Props = {
  activeSettings: ActiveSettingsType,
  affiliate: string,
  allErrors: Array<AllErrorType>,
  clearNotifications: () => void,
  debug: boolean,
  defaultValuesRequestLoaderOff: (string, string, boolean) => void,
  discountResources: ResourcesType,
  discounts: { [key: string]: any },
  duplicatedLink: string,
  errors: Array<AllErrorType>,
  errorTranslations: { [key: string]: string },
  fetching: number,
  generalValidationErrors: { [key: string]: { [key: string]: string | number } },
  getDiscountContentRequest: Function,
  getDiscountResourcesRequest: Function,
  history: any,
  initialData: boolean,
  isBackOffice: boolean,
  lastValidStep: Object,
  linkId: string,
  location: LocationType,
  notificationToShow: Array<NotificationType>,
  onChangeDebugMode: (debug: boolean) => void,
  onChangeDiscountErrors: (ValidationErrorType) => void,
  onChangeRedirectUrl: (redirectUrl: string) => void,
  onChangeTestingMode: (testing: boolean) => void,
  onChangeWebType: (webType: number) => void,
  onGetDefaultContentRequestLoaderOff: () => void,
  onGetWebSettingsRequest: () => void,
  onResetLastValidStepExpire: () => void,
  onSetLastValidStepIndex: (key: string) => void,
  onSetProductName: (productName: string) => void,
  onSetReferrer: (referrer: string) => void,
  productContentRequestLoaderOff: (product: string, isBackOffice: boolean) => void,
  productName: string,
  productResourcesRequestLoaderOff: (product: string) => void,
  products: Array<Product>,
  quoteFiltersRequest: (product: string) => void,
  redirectUrl: string,
  rehydrated: boolean,
  resetDiscounts: () => void,
  resetFilters: () => void,
  restoreDataRequest: (string) => void,
  setAffiliate: (string) => void,
  setDuplicatedLink: (string) => void,
  setIsBackOffice: (boolean) => void,
  setSubProductName: (string) => void,
  steps: StepsType,
  subProductName: string,
  testing: boolean,
  token: string,
  trackingParams: Object,
  translations: { [key: string]: string },
  unsetInitialData: () => void,
  webType: number,
}

type State = {
  showDebugVariables: boolean,
  discountScriptLoaded: boolean,
  rehydrated: boolean,
}

const _valuesBeforeHydration: Object = { policyHolder: {} }

class RootContainer extends React.Component<Props, State> {
  _requestsAlreadySent: boolean = false
  _productName: string

  constructor(props) {
    super(props)

    // only after deployment
    const options = {
      appCodeName: navigator.appCodeName,
      appName: navigator.appName,
      appVersion: navigator.appVersion,
      cookieEnabled: navigator.cookieEnabled,
      language: navigator.language,
      onLine: navigator.onLine,
      platform: navigator.platform,
      product: navigator.product,
      productSub: navigator.productSub,
      userAgent: navigator.userAgent,
      vendor: navigator.vendor,
      vendorSub: navigator.vendorSub,
    }
    GuardXLogger.config(`https://guardx.devx.agency/${config.getConfig('guardXApiKey')}`,
      {
        debug: DEV,
        release: packageJson.version,
        tags: {
          version: packageJson.version,
          ...options
        }
      }
    )
  }

  state = {
    showDebugVariables: false,
    discountScriptLoaded: false,
    rehydrated: false,
  }

  static getDerivedStateFromProps(nextProps: Props, prevState: State) {
    if (nextProps.redirectUrl !== '') {
      nextProps.onChangeRedirectUrl('')
      nextProps.history.push(nextProps.redirectUrl, nextProps.redirectState ? nextProps.redirectState : undefined)
    }

    I18n.setTexts({
      ...config.getDefaultTranslations(),
      ...I18n.texts,
      ...nextProps.translations,
      validationErrors: nextProps.errorTranslations
    })

    if (!nextProps.location.pathname.includes('error')) {
      for (let index = 0; index < nextProps.errors.length; index++) {
        if (nextProps.errors[index].type === ERROR_TYPES.CRITICAL) {
          nextProps.onResetLastValidStepExpire()
          nextProps.onChangeRedirectUrl('/error')
          break
        }
      }
    }
    if (nextProps.rehydrated && !prevState.rehydrated) {
      return { rehydrated: true }
    }
    return null
  }

  shouldComponentUpdate(nextProps: Props, nextState: State) {
    // nextProps.errors is not used in render
    // nextProps.redirectUrl is not used in render
    // nextProps.discountResources is not used in render
    // nextProps.errorTranslations is not used in render
    // nextProps.initialData is not used in render
    // nextProps.isBackOffice is not changing
    return JSON.stringify([nextProps.trackingParams, nextProps.discounts, nextProps.generalValidationErrors,
        nextProps.notificationToShow, nextProps.steps, nextProps.lastValidStep, nextProps.translations, nextProps.errorTranslations,
        nextProps.location]) !==
      JSON.stringify([this.props.trackingParams, this.props.discounts, this.props.generalValidationErrors,
        this.props.notificationToShow, this.props.steps, this.props.lastValidStep, this.props.translations,
        this.props.errorTranslations, this.props.location]) ||
      nextState.rehydrated !== this.state.rehydrated ||
      nextProps.fetching !== this.props.fetching ||
      nextProps.productName !== this.props.productName ||
      nextProps.testing !== this.props.testing ||
      nextProps.linkId !== this.props.linkId ||
      nextState.discountScriptLoaded !== this.state.discountScriptLoaded ||
      nextProps.webType !== this.props.webType ||
      nextProps.debug && (
        JSON.stringify([nextProps.activeSettings, nextProps.allErrors]) !==
        JSON.stringify([this.props.activeSettings, this.props.allErrors]) ||
        nextState.showDebugVariables !== this.state.showDebugVariables
      ) ||
      nextProps.isBackOffice && (nextProps.duplicatedLink !== this.props.duplicatedLink)
  }

  componentDidMount = () => {
    this.initValues()
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (typeof window.scrollTo === 'function' &&
      (Object.keys(this.props.generalValidationErrors).length > Object.keys(prevProps.generalValidationErrors).length ||
        this.props.location !== prevProps.location)) {
      window.scrollTo(0, 0)
    }
    if (JSON.stringify(this.props.trackingParams) !== JSON.stringify(prevProps.trackingParams)) {
      this.saveUtmParams()
    }

    if (!prevState.rehydrated && this.state.rehydrated) {
      this.initValues()
    }
  }

  initValues = () => {
    let domain = cookie.readCookie('suriSite')
    if (!domain)
      domain = location.host

    if (domain.indexOf('sro') >= 0)
      this.props.onChangeWebType(WEB_TYPES.SRO)
    else if (domain.indexOf('sur') >= 0)
      this.props.onChangeWebType(WEB_TYPES.SUR)
    else if (domain.indexOf('pov') >= 0)
      this.props.onChangeWebType(WEB_TYPES.POV)
    else if (domain.indexOf('renomia') >= 0)
      this.props.onChangeWebType(WEB_TYPES.REN)
    else
      this.props.onChangeWebType(WEB_TYPES.SUR)

    if (domain !== document.referrer.split('/')[2]) {
      console.log(document.referrer)
      this.props.onSetReferrer(document.referrer)
    }

    this.saveUtmParams()

    this._productName = config.getConfig('productName')
    if (this._productName) {
      this.props.onSetProductName(this._productName)
    }

    const debug = cookie.readCookie('suriDebug')
    if (debug && debug === passwords.debug) {
      this.props.onChangeDebugMode(true)
    }

    const testing = cookie.readCookie('suriTesting')
    if (testing && testing === passwords.testing) {
      this.props.onChangeTestingMode(true)
    }

    const backOffice = config.getConfig('isBackOffice')
    this.props.setIsBackOffice(!!backOffice)
    if (backOffice) {
      this.props.quoteFiltersRequest(this._productName)
    } else {
      this.props.resetFilters()
    }

    if (this.props.subProductName) {
      this.props.setSubProductName(this.props.subProductName)
    }

    if (this.state.rehydrated) {
      if (this.props.initialData) {
        this.props.defaultValuesRequestLoaderOff(this._productName, this.props.subProductName, backOffice)
      }
    }

    this.props.onGetDefaultContentRequestLoaderOff()
    this.getProductContent(this._productName)

    if (this.props.location.state && this.props.location.state.message) {
      this.props.history.replace({ ...this.props.location, state: undefined })
    }

    this.checkStep()

    if (backOffice) {
      this.props.getDiscountResourcesRequest(this._productName)
      this.props.getDiscountContentRequest(this._productName)
    }
    if (this.props.affiliate) {
      this.props.setAffiliate(this.props.affiliate)
    } else if (cookie.readCookie('affiliate')) {
      this.props.setAffiliate(cookie.readCookie('affiliate'))
    }
  }

  componentDidCatch(error, errorInfo) {
    GuardXLogger.captureException(error, { extra: errorInfo })
  }

  saveUtmParams = () => {
    const { trackingParams } = this.props

    let utmSource = cookie.readCookie('utm_source')
    let utmMedium = cookie.readCookie('utm_medium')
    let utmCampaign = cookie.readCookie('utm_campaign')
    let utmContent = cookie.readCookie('utm_content')
    let utmTerm = cookie.readCookie('utm_term')
    let googleClientId = cookie.readCookie('_ga')
    if (typeof googleClientId === 'string' && googleClientId.split('.').length > 2) {
      googleClientId = googleClientId.split('.').slice(2).join('.')
    }

    if (trackingParams) {
      if (trackingParams.googleClientId) {
        googleClientId = trackingParams.googleClientId
      }
      if (trackingParams.properties && trackingParams.properties['utm-source']) {
        utmSource = trackingParams.properties['utm-source']
        utmMedium = trackingParams.properties['utm-medium']
        utmCampaign = trackingParams.properties['utm-campaign']
        utmContent = trackingParams.properties['utm-content']
        utmTerm = trackingParams.properties['utm-term']
      }
    }

    if (googleClientId) {
      Api.setHeader('X-Tracking-googleClientId', googleClientId)
    }

    if (utmSource) {
      Api.setHeader('X-Tracking-utm-source', utmSource)
      if (utmMedium) {
        Api.setHeader('X-Tracking-utm-medium', utmMedium)
      }
      if (utmCampaign) {
        Api.setHeader('X-Tracking-utm-campaign', utmCampaign)
      }
      if (utmContent) {
        Api.setHeader('X-Tracking-utm-content', utmContent)
      }
      if (utmTerm) {
        Api.setHeader('X-Tracking-utm-term', utmTerm)
      }
    }
  }

  getProductContent = (productName) => {
    if (this.state.rehydrated) {
      const product = productName ? productName : this.props.productName
      this.props.productContentRequestLoaderOff(product, this.props.isBackOffice)
      this.props.productResourcesRequestLoaderOff(product)
    }
  }

  checkStep = () => {
    if (!this.props.lastValidStep.expire) {
      for (let index = (this.props.lastValidStep.index + 1); index < this.props.steps.length; index++) {
        if (this.props.location.pathname.indexOf(this.props.steps[index].key) >= 0) {
          this.props.onChangeRedirectUrl(this.props.steps[this.props.lastValidStep.index].url)
          break
        }
      }
    } else if (new Date(this.props.lastValidStep.expire).getTime() > Date.now()) {
      this.props.onChangeRedirectUrl(this.props.steps[this.props.lastValidStep.index].url)
    } else {
      this.props.onResetLastValidStepExpire()
      this.props.onSetLastValidStepIndex('product')
    }
  }

  handleToggleDebugModal = () => {
    this.setState({ showDebugVariables: !this.state.showDebugVariables })
  }

  handleToggleNotifyModal = () => {
    this.props.clearNotifications()
  }

  handleFetchDiscountResources = (resourceName: string): void | ResourceType => {
    const { discountResources } = this.props
    const resource = getValueForPath(resourceName, 0, discountResources)
    if (resource) {
      return resource
    }
  }

  handleDiscountScriptLoad = () => {
    this.setState({ discountScriptLoaded: true })
  }

  handleDiscountValidationErrors = (discountErrors: ValidationErrorType) => {
  }

  render() {
    const {
      isBackOffice, steps, lastValidStep, generalValidationErrors, productName, token, testing, debug, notificationToShow, fetching,
      subProductName, webType
    } = this.props

    /* if (!I18n.texts || !I18n.texts.translationLoaded || !I18n.texts.validationErrors ||
      !I18n.texts.validationErrors.errorTranslationLoaded) {
      return (
        <div className='body-full'>
          <Loader active fullScreen/>
        </div>
      )
    } */

    const classes = ['container']
    if (debug) {
      classes.push('mt-5')
    }
    const activeStepKey = steps.reduce((acc, step) => {
      if (this.props.location.pathname.replace(/\/$/, '') === getStepUrl(subProductName, step.key)) {
        return step.key
      } else if (this.props.location.pathname.includes('error')) {
        return 'error'
      }
      return acc
    }, '')

    const textsLoaded = I18n.texts.general && I18n.texts.general.stepInfo && I18n.texts.general.stepInfo[activeStepKey]

    return (
      <div className='body-full'>
        {testing && <p>version: {packageJson.version}</p>}
        {DEV && <div className='container mb-4'>
          <div className='alert alert-sm alert-with-icon alert-warning m-auto' role='alert'>
            <i className='icon icon-checkmark'/><span>TEST version: {packageJson.version}, URL: {API_LINK}</span>
          </div>
        </div>}
        <div className={classNames(classes)}>
          {textsLoaded && !!I18n.texts.general.stepInfo[activeStepKey].top &&
          <div className='text-center py-4'>
            <span className='h5 font-weight-normal'>
              <span className='pfp pfp-annual align-middle'/>&nbsp;{I18n.translate(`general.stepInfo.${activeStepKey}.top`)}
            </span>
          </div>
          }
          {!['product', 'error', 'accept'].includes(activeStepKey) &&
          <div className='mt-3'>
            <NavigationPanel
              step={lastValidStep}
              steps={steps}
              subProductName={subProductName}
              url={this.props.location.pathname}
            />
          </div>
          }
          {(this.props.location.state && this.props.location.state.message || Object.keys(generalValidationErrors).length > 0) &&
          <div className='mb-3 mt-3'>
            {this.props.location.state && this.props.location.state.message &&
            <div className='alert alert-danger'>
              {I18n.translate(`validationErrors.${this.props.location.state.message}`,
                { phoneNumber: I18n.translate('acceptScreen.phoneNumber', { context: webType }) })}
            </div>}
            {Object.keys(generalValidationErrors).map(errorKey => {
              return (
                <div className='alert alert-danger' key={errorKey}>
                  {I18n.translate(`validationErrors.${errorKey}`)}
                </div>
              )
            })}
          </div>
          }
        </div>
        <Switch>
          <Route
            exact
            path='/'
            render={props => {
              return (
                <ProductDetailContainer
                  {...props}
                  beforeHydration={_valuesBeforeHydration}
                  discountScriptLoaded={this.state.discountScriptLoaded}
                  fetchDiscountResources={this.handleFetchDiscountResources}
                  location={props.location}
                  onDiscountValidationErrors={this.handleDiscountValidationErrors}
                  rehydrated={this.state.rehydrated}
                  saveUtmParams={this.saveUtmParams}
                  token={token}
                />
              )
            }}
          />
          <Route
            path={getStepUrl(subProductName, 'calculator')}
            render={props => (
              <CalculatorDetailContainer
                {...props}
                discountScriptLoaded={this.state.discountScriptLoaded}
                fetchDiscountResources={this.handleFetchDiscountResources}
                onDiscountValidationErrors={this.handleDiscountValidationErrors}
                rehydrated={this.state.rehydrated}
                saveUtmParams={this.saveUtmParams}
                token={token}
              />
            )}
          />
          <Route
            path={getStepUrl(subProductName, 'quotes')}
            render={props => (
              <QuoteListContainer
                {...props}
                discountScriptLoaded={this.state.discountScriptLoaded}
                fetchDiscountResources={this.handleFetchDiscountResources}
                onDiscountValidationErrors={this.handleDiscountValidationErrors}
                rehydrated={this.state.rehydrated}
                saveUtmParams={this.saveUtmParams}
                token={token}
              />
            )}
          />
          <Route
            path={getStepUrl(subProductName, 'contract')}
            render={props => (
              <ContractDraftContainer
                {...props}
                discountScriptLoaded={this.state.discountScriptLoaded}
                fetchDiscountResources={this.handleFetchDiscountResources}
                onDiscountValidationErrors={this.handleDiscountValidationErrors}
                rehydrated={this.state.rehydrated}
                saveUtmParams={this.saveUtmParams}
                token={token}
              />
            )}
          />
          <Route
            path={getStepUrl(subProductName, 'accept')}
            render={props => (
              <AcceptDraftContainer
                {...props}
                discountScriptLoaded={this.state.discountScriptLoaded}
                fetchDiscountResources={this.handleFetchDiscountResources}
                onDiscountValidationErrors={this.handleDiscountValidationErrors}
                rehydrated={this.state.rehydrated}
                token={token}
              />
            )}
          />
          <Route
            path='/error'
            render={props => (
              <ErrorContainer
                {...props}
                discountScriptLoaded={this.state.discountScriptLoaded}
                fetchDiscountResources={this.handleFetchDiscountResources}
                onDiscountValidationErrors={this.handleDiscountValidationErrors}
                token={token}
              />
            )}
          />
          <Redirect to='/'/>
        </Switch>
        {textsLoaded && !!I18n.texts.general.stepInfo[activeStepKey].bottom &&
        <div className='text-center py-4'>
            <span className='h5 font-weight-normal'>
              <span className='pfp pfp-annual align-middle'/>&nbsp;{I18n.translate(`general.stepInfo.${activeStepKey}.bottom`)}
            </span>
        </div>
        }
        {isBackOffice &&
        <ValidatorScript
          handleScriptCreate={(e) => {
            if (debug) {
              console.error(e)
            }
          }}
          handleScriptError={(e) => {
            if (debug) {
              console.error(e)
            }
          }}
          handleScriptLoad={this.handleDiscountScriptLoad}
          id='discount-validator'
          shouldRemove
          url={`${serverUrl}/product/${productName}/discount-validator.js?access_token=${token}`}
        />
        }
        {debug &&
        <Alert className='fixed-top w-25 text-center bg-light' color='info' style={{ right: 0, left: 'auto' }}>
          <div>
            Aplikace běží v Debug módu.<br/>
            WebType: {WEB_TYPES_KEYS[this.props.webType]}. <br/>
            BackOffice: {this.props.isBackOffice ? 'ano' : 'ne'}<br/>
            <Button
              className='mt-2'
              color='info'
              onClick={this.handleToggleDebugModal}
              outline
              size='sm'
            >Zobrazit aktivní nastavení</Button>
          </div>
        </Alert>}
        <Modal isOpen={this.state.showDebugVariables} toggle={this.handleToggleDebugModal}>
          <ModalBody>
            <VariablesDebugBlock
              active={debug}
              title='activeSettings'
              variables={{...this.props.activeSettings, rehydrated: this.state.rehydrated}}
            />
          </ModalBody>
        </Modal>
        <Modal
          className='d-flex vh-100 align-items-center mt-0'
          contentClassName='p-0 bg-transparent border-0 m-0 shadow-none'
          isOpen={notificationToShow.length > 0}
          toggle={this.handleToggleNotifyModal}
        >
          {notificationToShow.map((notification: NotificationType, index: number) => {
            const alertClasses = ['mx-auto', 'alert', 'alert-sm', 'alert-with-icon', 'bg-white cursor-pointer']
            const iconClasses = ['icon']
            switch (notification.type) {
              case NOTIFICATION_TYPES.success:
                alertClasses.push('alert-success')
                iconClasses.push('icon-checkmark')
                break
              case NOTIFICATION_TYPES.info:
                alertClasses.push('alert-active-object', 'text-dark')
                iconClasses.push('pfp', 'pfp-info')
                break
              case NOTIFICATION_TYPES.error:
                alertClasses.push('alert-danger')
                iconClasses.push('pfp', 'pfp-close')
                break
            }
            return (
              <div
                className={classNames(alertClasses)}
                key={`alert-${index}`}
                onClick={this.handleToggleNotifyModal}
                role='alert'
              >
                <i className={classNames(iconClasses)}></i>
                <span>
                    {notification.title && <strong>{notification.title}: </strong>}
                  {notification.message}
                  </span>
              </div>
            )
          })}
        </Modal>
        {debug &&
        <div>
          {this.props.allErrors.map((error) => (
            <ShowDebugError
              active={debug}
              error={error}
              key={error.time}
            />
          ))}
        </div>
        }
        <ValidatorError scroll={Object.keys(this.props.generalValidationErrors).length > 0}/>
        <Loader active={fetching > 0} fullScreen/>
        <input
          id='currentStep'
          type='hidden'
          value={activeStepKey}
        />
        <GoogleTagManager id='GTM-MSZ99BX'/>
      </div>
    )
  }
}

const mapStateToProps = oldState => {
  return (
    getProductState(oldState, (state) => {
      return ({
        activeSettings: state.settings.activeSettings,
        allErrors: state.general.allErrors,
        debug: state.debug.debug,
        discountResources: state.discounts.discountResources,
        discounts: state.discounts.discounts,
        duplicatedLink: state.linkBuilder.duplicatedLink,
        errors: state.general.errors,
        errorTranslations: state.general.errorTranslations,
        fetching: state.general.fetching,
        generalValidationErrors: state.validation.generalValidationErrors,
        initialData: state.userData.initialData,
        isBackOffice: config.getConfig('isBackOffice'),
        lastValidStep: state.navigation.lastValidStep,
        notificationToShow: state.general.notificationToShow,
        productName: state.product.productName,
        redirectState: state.general.redirectState,
        redirectUrl: state.general.redirectUrl,
        rehydrated: state._persist.rehydrated,
        steps: state.navigation.steps,
        testing: state.debug.testing,
        trackingParams: state.userData.trackingParams,
        translations: state.general.translations,
        webType: state.settings.webType,
      })
    })
  )
}

const mapDispatchToProps = {
  clearNotifications: GeneralActions.clearNotifications,
  defaultValuesRequestLoaderOff: UserDataActions.defaultValuesRequestLoaderOff,
  getDiscountContentRequest: DiscountsActions.getDiscountContentRequest,
  getDiscountResourcesRequest: DiscountsActions.getDiscountResourcesRequest,
  onChangeDebugMode: DebugActions.onChangeDebugMode,
  onChangeDiscountErrors: ValidationActions.onChangeDiscountErrors,
  onChangeRedirectUrl: GeneralActions.onChangeRedirectUrl,
  onChangeTestingMode: DebugActions.onChangeTestingMode,
  onChangeWebType: SettingsActions.onChangeWebType,
  onGetDefaultContentRequestLoaderOff: GeneralActions.onGetDefaultContentRequestLoaderOff,
  onResetLastValidStepExpire: NavigationActions.onResetLastValidStepExpire,
  onSetLastValidStepIndex: NavigationActions.onSetLastValidStepIndex,
  onSetProductName: ProductActions.onSetProductName,
  onSetReferrer: NavigationActions.onSetReferrer,
  productContentRequestLoaderOff: ProductActions.productContentRequestLoaderOff,
  productResourcesRequestLoaderOff: ProductActions.productResourcesRequestLoaderOff,
  quoteFiltersRequest: ProductActions.quoteFiltersRequest,
  resetDiscounts: DiscountsActions.resetDiscounts,
  resetFilters: QuoteActions.resetFilters,
  restoreDataRequest: UserDataActions.restoreDataRequest,
  setAffiliate: ProductActions.setAffiliate,
  setDuplicatedLink: LinkBuilderActions.setDuplicatedLink,
  setIsBackOffice: UserDataActions.setIsBackOffice,
  setSubProductName: ProductActions.setSubProductName,
  unsetInitialData: UserDataActions.unsetInitialData,
}

export default connect(mapStateToProps, mapDispatchToProps)(RootContainer)
